import React, { useState, useEffect, useRef } from "react";

import "./stylesMonthlyChallenges.css";

import KpiCard from "../../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";

import { DownOutlined, UpOutlined } from "@ant-design/icons";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";

// Challenge ID to progress field mapping
const CHALLENGE_PROGRESS_MAP = {
  '3fd10f38-13de-4ed0-8422-40bb2e02b6ae': 'num_new_followers',
  '4f2635ad-6fb6-4945-94a3-e5a7954b120a': 'num_users_referred_current_month',
  'e0b81434-390f-4490-aa03-f31de2e39e9a': 'consecutive_days_streak',
  '78c94c1b-0025-486a-8fbc-e87988b8e452': 'p2p_wins_count',
  '3e62a3f5-a16b-4c07-a9ed-5f76cc5f1665': 'num_daily_challenges_completed',
};

function MonthlyChallenges({ userData }) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [monthlyChallengesData, setMonthlyChallengesData] = useState({});
  const [isApiRunning, setIsApiRunning] = useState(false);
  const hasInitializedExpandedRows = useRef(false);

  const kpis = [
    {
      title: "Active Days",
      value: monthlyChallengesData?.activity_days || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Consecutive Days",
      value: monthlyChallengesData?.consecutive_days_streak || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Initial Followers",
      value: monthlyChallengesData?.initial_follower_count || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Current Followers",
      value: monthlyChallengesData?.current_followers_count || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Initial Referrals",
      value: monthlyChallengesData?.initial_num_users_referred || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Current Referrals",
      value: monthlyChallengesData?.num_users_referred_current_month || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "p2p Wins",
      value: monthlyChallengesData?.p2p_wins_count || 0,
      maximumFractionDigits: 0,
    },
    {
      title: "Daily challenges",
      value: monthlyChallengesData?.num_daily_challenges_completed || 0,
      maximumFractionDigits: 0,
    },
  ];

  const toggleExpand = (key) => {
    console.log("expanding row", key, expandedRowKeys);
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
    } else {
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  };

  const isExpanded = (key) => expandedRowKeys.includes(key);

  const getAllMonthlyChallenges = async () => {
    setIsApiRunning(true);
    const params = {
      user_id: userData?.user_id,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES.GET_MONTHLY_CHALLENGE_INFO,
        params
      );
      console.log("[MonthlyChallenges] response:", response?.data?.data);

      setMonthlyChallengesData(response?.data?.data);
    } catch (error) {
      console.log("[MonthlyChallenges] error:", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  useEffect(() => {
    getAllMonthlyChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkProgress = (challengeId, challenge) => {
    const progressKey = CHALLENGE_PROGRESS_MAP[challengeId];
    return progressKey ? challenge?.[progressKey] : 0;
  };

  // Transform challenges_progress object into array for table rendering
  const transformedChallenges = React.useMemo(() => {
    const challengesProgress = monthlyChallengesData?.challenges_progress;

    if (!challengesProgress) return [];

    return Object.entries(challengesProgress).map(
      ([challengeId, challenge]) => {
        // Calculate overall progress for the challenge
        const completedTasks =
          challenge.tasks?.filter((task) => task.completed).length || 0;
        const totalTasks = challenge.tasks?.length || 0;

        return {
          key: challengeId,
          challenge_id: challenge.challenge_id,
          challenge_name: challenge.challenge_name,
          description: challenge.challenge_description,
          completed: challenge.completed ? "Yes" : "No",
          progress: `${completedTasks}/${totalTasks}`,
          reward:
            challenge.tasks?.[challenge.tasks.length - 1]?.reward
              ?.reward_display_name || "N/A",
          tiers:
            challenge.tasks?.map((task, index) => ({
              key: `${challengeId}-${task.id}`,
              challenge_name: `Tier ${index + 1}`,
              description: task.name,
              completed: task.completed ? "Yes" : "No",
              progress: `${checkProgress(challengeId, monthlyChallengesData)}/${task.milestone}` ,
              reward: task.reward?.reward_display_name || "N/A",
            })) || [],
        };
      }
    );
  }, [monthlyChallengesData]);

  // Expand all rows initially when data loads (only once)
  useEffect(() => {
    if (transformedChallenges.length > 0 && !hasInitializedExpandedRows.current) {
      const allKeys = transformedChallenges.map((challenge) => challenge.key);
      setExpandedRowKeys(allKeys);
      hasInitializedExpandedRows.current = true;
    }
  }, [transformedChallenges]);

  return (
    <div className="user-manager-game-pass-body-monthly-challenges">
      <div className="user-manager-game-pass-body-monthly-challenges-kpis">
        <div className="user-manager-game-pass-body-monthly-challenges-kpis-kpi-cards">
          {kpis.map((kpi, index) => (
            <KpiCard
              key={index}
              title={kpi.title}
              value={kpi.value}
              backgroundColor="#1A1A22"
              maximumFractionDigits={kpi.maximumFractionDigits}
              isApiRunning={isApiRunning}
            />
          ))}
        </div>
      </div>
      <div className="user-manager-game-pass-body-monthly-challenges-table-container">
        <h2>Monthly Challenge Status</h2>
        {isApiRunning && <RebetLoader width={"100px"} height={"100px"} />}
        {!isApiRunning && (
          <div className="user-manager-game-pass-body-monthly-challenges-table">
            <table className="monthly-challenges-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Challenge ID</th>
                  <th>Challenge Name</th>
                  <th>Description</th>
                  <th>Completed</th>
                  <th>Progress</th>
                  <th>Reward</th>
                </tr>
              </thead>
              <tbody>
                {transformedChallenges.map((challenge) => (
                  <React.Fragment key={challenge.key}>
                    <tr className="main-row">
                      <td className="expand-icon">
                        {isExpanded(challenge.key) ? (
                          <UpOutlined
                            onClick={() => toggleExpand(challenge.key)}
                          />
                        ) : (
                          <DownOutlined
                            onClick={() => toggleExpand(challenge.key)}
                          />
                        )}
                      </td>
                      <td>{challenge.challenge_id}</td>
                      <td>{challenge.challenge_name}</td>
                      <td>{challenge.description}</td>
                      <td>
                        <div
                          className={`user-manager-game-pass-body-monthly-challenges-table-completed ${challenge.completed
                            .toLowerCase()
                            .replace(" ", "-")}`}
                        >
                          {challenge.completed}
                        </div>
                      </td>
                      <td>{challenge.progress}</td>
                      <td></td>
                    </tr>
                    {isExpanded(challenge.key) &&
                      challenge.tiers.map((tier) => (
                        <tr key={tier.key} className="tier-row">
                          <td></td>
                          <td></td>
                          <td>{tier.challenge_name}</td>
                          <td>{tier.description}</td>
                          <td>
                            <div
                              className={`user-manager-game-pass-body-monthly-challenges-table-completed ${tier.completed.toLowerCase()}`}
                            >
                              {tier.completed}
                            </div>
                          </td>
                          <td>{tier.progress}</td>
                          <td>{tier.reward}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthlyChallenges;
